import { useAuth } from './lib/auth'
import Login from './pages/Login'
import Shell from './pages/Shell'
import { ErrorBoundary } from './components/ErrorBoundary'
import { InviteHandler } from './components/InviteHandler'
import { RealtimeBridge } from './lib/realtimeBridge'
import { useEnrichmentTeam, useSharedTeam } from './hooks/useTeam'
import Privacy from './pages/Privacy'
import ExtensionEnrichmentCapabilityRelay from './components/ExtensionEnrichmentCapabilityRelay'

/**
 * Inner component that sits inside the session-authed branch. Needed so we
 * can call team hooks without dragging them into the un-authed login flow.
 * Shared team id is read here so the `RealtimeBridge` rebuilds its
 * team-scoped subscription when the user joins or leaves a team.
 */
function AuthedApp({ userId }: { userId: string }) {
  const sharedTeam = useSharedTeam()
  const enrichmentTeam = useEnrichmentTeam()
  return (
    <ErrorBoundary name="shell">
      {/* Single Supabase Realtime channel fans out into TanStack Query
          invalidations for every table-backed hook. Replaces the per-hook
          channels that used to open N WebSockets per tab. */}
      <RealtimeBridge userId={userId} sharedTeamId={sharedTeam?.id ?? null} />
      <ExtensionEnrichmentCapabilityRelay teamId={enrichmentTeam?.id ?? null} />
      <InviteHandler />
      <Shell />
    </ErrorBoundary>
  )
}

function SessionApp() {
  const { session, loading, user } = useAuth()

  if (loading) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-zinc-50">
        <div className="text-sm text-zinc-400">Loading...</div>
      </div>
    )
  }

  return session && user ? (
    <AuthedApp userId={user.id} />
  ) : (
    <ErrorBoundary name="page:login">
      <Login />
    </ErrorBoundary>
  )
}

export default function App() {
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/'

  if (pathname === '/privacy') {
    return (
      <ErrorBoundary name="page:privacy">
        <Privacy />
      </ErrorBoundary>
    )
  }

  return <SessionApp />
}
